import { Drawer, message, Popover } from "antd";
import styles from "./AddQuarryDrawer.module.scss";
import {
  createQuarryPayload,
  DaySwitchVals,
  DrawerConstants,
  DrawerMessages,
  getDayTmeFields,
  getExistingDays,
  getFormFields,
} from "./constants";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import InputField from "../../../reusable-components/TNInputField";
import TNGoogleAddressSuggestions from "../../../reusable-components/TNGoogleSearch";
import { useFormContext } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import TNButton from "../../../reusable-components/TNButton";
import { ButtonType } from "../../../enums/ButtonAttributesEnums";
import { handleSubmit } from "./validation";
import { arrayToOptions } from "../../../utils/getDropdownOptions";
import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { AddQuarryParams } from "../../../Types/quarryDatabaseTypes";
import { ModalModes } from "../../../enums/DatabaseEnums";
import copyIcon from "../../../assets/images/copy-times.svg";
import {
  formatNumber,
  formatPhoneNumber,
} from "../../../utils/numberFormatterUtil";
import CustomerDatabaseService from "../../../services/customerDatabase.service";
import { useHistory } from "react-router-dom";
import { allowedKeys } from "../../../constants/keyboardKeys";
import { stripCurrencyFormatting } from "../../../Utilities/regex";
import { appRoutes } from "../../../constants/appRoutes";

interface AddQuarryDrawerProps {
  isOpen: boolean;
  handleCloseDrawer?: () => void;
  submitAction?: UseMutateAsyncFunction<any, Error, AddQuarryParams, unknown>;
  submitStatus?: boolean;
  quarryModalMode?: ModalModes;
  setQuarryModalMode?: () => void;
  quarryId?: string;
  profileView?: boolean;
  handleViewNavigate?: (record: object) => void;
}

const AddQuarryDrawer = (props?: AddQuarryDrawerProps) => {
  const { GetAccountTermsOptions } = CustomerDatabaseService();
  const { mutateAsync } = GetAccountTermsOptions({ isCrm: false });
  const [accountTermOptions, setAccountTermOptions] = useState([]);
  const navigate = useHistory();
  const { watch, getValues, setError, clearErrors, reset, setValue, formState: { defaultValues } } =
    useFormContext();
  const {
    isOpen,
    handleCloseDrawer,
    submitAction,
    submitStatus,
    quarryModalMode,
    setQuarryModalMode,
    quarryId,
    profileView,
    handleViewNavigate,
  } = props;
  const {
    WIDTH,
    ADD_QUARRY_LABEL,
    EDIT_QUARRY_LABEL,
    COORDINATES_LABEL,
    CANCEL_LABEL,
    SUBMIT_AND_VIEW_LABEL,
    SUBMIT_LABEL,
  } = DrawerConstants;

  const disableCoordinatesValue = watch("disableCoordinates");
  const accountTermsValue = watch("accountTerms");

  const handleCancel = () => {
    const listOfDays = getExistingDays(defaultValues);
    setDaySwitchVals(listOfDays);
    clearErrors();
    reset();
    handleCloseDrawer?.();
    setQuarryModalMode(ModalModes.CREATE);
  };

  const handleSubmitAction = async (viewProfile: boolean) => {
    const errors = handleSubmit({
      formValues: getValues(),
      daySwitchVals,
      disableCoordinatesValue,
    });
    if (errors?.length) {
      errors?.map((err) =>
        setError(err?.field, {
          message: err?.message,
        })
      );
    } else {
      const payload = createQuarryPayload(
        getValues(),
        daySwitchVals,
        disableCoordinatesValue
      );
      await submitAction(
        {
          id: quarryId,
          ...payload,
        },
        {
          onSuccess: (res) => {
            if (quarryModalMode === ModalModes?.EDIT) {
              message.success(DrawerMessages.QUARRY_UPDATED_SUCCESS);
              if (profileView) {
                const locationProp = {
                  quarryName: payload?.quarryName,
                };
                navigate.replace(window.location.pathname, locationProp);
              }
            } else message.success(DrawerMessages.QUARRY_ADDED_SUCCESS);
            if (viewProfile) {
              const navigateParams = {
                quarryName: payload?.quarryName,
                id: res?.quarryId,
              };
              handleViewNavigate?.(navigateParams);
            }
            setDaySwitchVals({
              monday: false,
              tuesday: false,
              wednesday: false,
              thursday: false,
              friday: false,
              saturday: false,
              sunday: false,
            });
            handleCancel();
          },
          onError: (error) => {
            if (
              DrawerMessages.ERROR_MESSAGES.includes(
                error?.response?.data?.message
              )
            ) {
              message.error(error?.response?.data?.message);
              return;
            }
            message.error(DrawerMessages.QUARRY_ADD_FAILED);
          },
        }
      );
    }
  };

  const DrawerHeader = (
    <span className={styles.drawerTitle}>
      <ConditionalRender
        fallback={ADD_QUARRY_LABEL}
        visible={quarryModalMode === ModalModes.EDIT}
      >
        {EDIT_QUARRY_LABEL}
      </ConditionalRender>
    </span>
  );

  const DrawerFooter = (
    <div className={styles.drawerFooter}>
      <TNButton
        text={
          quarryModalMode === ModalModes.CREATE ? SUBMIT_LABEL : CANCEL_LABEL
        }
        loading={submitStatus && quarryModalMode === ModalModes.CREATE}
        type={ButtonType.DEFAULT}
        onClick={() => {
          if (quarryModalMode === ModalModes.CREATE) handleSubmitAction(false);
          else handleCancel();
        }}
      />
      <TNButton
        text={
          quarryModalMode === ModalModes.CREATE
            ? SUBMIT_AND_VIEW_LABEL
            : SUBMIT_LABEL
        }
        type={ButtonType.PRIMARY}
        loading={submitStatus}
        onClick={() => {
          const isAdd = quarryModalMode === ModalModes.CREATE;
          handleSubmitAction(isAdd);
        }}
      />
    </div>
  );
  const formFields = getFormFields();

  const handlePasteTimes = (copyDay: string) => {
    const startTime = getValues(`${copyDay}StartTime`);
    const endTime = getValues(`${copyDay}EndTime`);

    if (!startTime || !endTime) {
      message.warning(DrawerMessages.SET_TIMES_FIRST_WARNING(copyDay));
      return;
    }

    const daysToUpdate = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    daysToUpdate.forEach((day) => {
      if (daySwitchVals[day]) {
        setValue(`${day}StartTime`, startTime, { shouldDirty: true });
        setValue(`${day}EndTime`, endTime, { shouldDirty: true });
      }
    });

    message.success(DrawerMessages.TIMES_COPIED_SUCCESS);
  };

  const getDaySwitch = (name: string) => {
    const [startTime, endTime] = [
      getValues(`${name}StartTime`),
      getValues(`${name}EndTime`),
    ];

    if (!startTime && !endTime) {
      return false;
    }
    return true;
  };

  const [daySwitchVals, setDaySwitchVals] = useState<{
    [key: string]: boolean;
  }>({
    monday: getDaySwitch("monday"),
    tuesday: getDaySwitch("tuesday"),
    wednesday: getDaySwitch("wednesday"),
    thursday: getDaySwitch("thursday"),
    friday: getDaySwitch("friday"),
    saturday: getDaySwitch("saturday"),
    sunday: getDaySwitch("sunday"),
  });

  const getSwitchVal = (name: string) => {
    const day = name?.toLowerCase();
    return daySwitchVals[day];
  };

  const setDaySwitchValue = (day: string, value: boolean) => {
    setDaySwitchVals((prevState) => ({
      ...prevState,
      [day]: value,
    }));
  };

  const dayTimeFields = getDayTmeFields({
    daySwitchVals,
    setDaySwitchValue,
  });

  const handlePhoneChange = useCallback((e) => {
    const formattedNumber = formatPhoneNumber(e);
    setValue("phone", formattedNumber);
  }, []);

  const handleNumberEntry = (e) => {
    // Allow control and navigation keys
    if (
      allowedKeys?.includes(e?.key) ||
      e?.ctrlKey ||
      e?.altKey ||
      e?.metaKey
    ) {
      return;
    }

    // Allow digitls
    if (/^\d$/.test(e?.key)) {
      return;
    }

    // Allow decimal point if enabled
    if (e?.key === ".") {
      const currentValue = e?.target?.value?.toString() || "";
      if (currentValue.includes(".")) {
        e?.preventDefault();
        return;
      }
      return;
    }

    e?.preventDefault();
  };

  const handleFetchPaymentTerms = async () => {
    await mutateAsync("", {
      onSuccess: (data) => {
        setAccountTermOptions(data ?? arrayToOptions(["Cash", "Custom Input"]));
      },
    });
  };

  useEffect(() => {
    if (!!isOpen) {
      handleFetchPaymentTerms();
    }
  }, [isOpen]);

  return (
    <Drawer
      visible={isOpen}
      onClose={handleCancel}
      destroyOnClose={true}
      className={styles.drawer}
      title={DrawerHeader}
      width={WIDTH}
      maskClosable={false}
      footer={DrawerFooter}
      destroyOnClose={profileView}
    >
      <div className={styles.formTopWrapper}>
        {formFields?.map((field) => (
          <>
            <ConditionalRender
              visible={
                field?.type === InputfieldTypes.ONCHANGE_TEXT &&
                field?.name === "phone"
              }
            >
              <InputField
                name={field?.name}
                label={field?.label}
                placeholder={field?.placeholder}
                type={InputfieldTypes.ONCHANGE_TEXT}
                onChange={handlePhoneChange}
                onBlur={(e) => {
                  if (e.target.value) {
                    clearErrors(field?.name);
                  }
                }}
              />
            </ConditionalRender>
            <ConditionalRender
              visible={
                field?.type === InputfieldTypes.TEXT &&
                field?.name !== "addressLat" &&
                field?.name !== "addressLng" &&
                field?.name !== "accountTermsCustom" &&
                field?.name !== "accountLimit"
              }
            >
              <InputField
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                type={InputfieldTypes.TEXT}
                onBlur={(e) => {
                  if (e.target.value) {
                    clearErrors(field?.name);
                  }
                }}
                disabled={!!field?.disabled}
              />
            </ConditionalRender>
            <ConditionalRender
              visible={
                field?.type === InputfieldTypes.TEXT &&
                field?.name === "accountLimit"
              }
            >
              <InputField
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                type={InputfieldTypes.TEXT}
                onKeyDown={handleNumberEntry}
                onBlur={(e) => {
                  if (e.target.value) {
                    const stripped = stripCurrencyFormatting(e.target.value);
                    const value = `$ ${formatNumber(
                      Number(stripped).toFixed(2)
                    )}`;
                    if (!isNaN(Number(stripped))) {
                      setValue(
                        "accountLimit",
                        `$ ${formatNumber(Number(stripped).toFixed(2))}`
                      );
                    } else {
                      setValue("accountLimit", null);
                    }
                    clearErrors(field?.name);
                  }
                }}
              />
            </ConditionalRender>
            <ConditionalRender
              visible={
                field?.type === InputfieldTypes.TEXT &&
                disableCoordinatesValue &&
                field?.name === "addressLng"
              }
            >
              <InputField
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                type={InputfieldTypes.TEXT}
                onBlur={(e) => {
                  if (e.target.value) {
                    clearErrors(field?.name);
                  }
                }}
              />
            </ConditionalRender>

            <ConditionalRender
              visible={
                field?.name === "addressField" && !disableCoordinatesValue
              }
            >
              <div>
                <div className={styles.disableCoordinatesField}>
                  {COORDINATES_LABEL}
                  <InputField
                    type={InputfieldTypes.SWITCH}
                    name={"disableCoordinates"}
                    onChange={(val) =>
                      setValue("disableCoordinates", val, { shouldDirty: true })
                    }
                    value={disableCoordinatesValue}
                    defaultSwitch={disableCoordinatesValue}
                  />
                </div>
                <TNGoogleAddressSuggestions
                  isFilterField
                  name={field.name}
                  label={field.label}
                  placeholder={field.placeholder}
                />
              </div>
            </ConditionalRender>
            <ConditionalRender
              visible={field?.name === "addressLat" && disableCoordinatesValue}
            >
              <div>
                <div className={styles.disableCoordinatesField}>
                  {COORDINATES_LABEL}
                  <InputField
                    type={InputfieldTypes.SWITCH}
                    name={"disableCoordinates"}
                    onChange={(val) =>
                      setValue("disableCoordinates", val, { shouldDirty: true })
                    }
                    value={disableCoordinatesValue}
                    defaultSwitch={disableCoordinatesValue}
                  />
                </div>
                <InputField
                  name={field.name}
                  label={field.label}
                  placeholder={field.placeholder}
                  type={InputfieldTypes.TEXT}
                  onBlur={(e) => {
                    if (e.target.value) {
                      clearErrors(field?.name);
                    }
                  }}
                />
              </div>
            </ConditionalRender>
            <ConditionalRender visible={field?.type === InputfieldTypes.SELECT}>
              <InputField
                type={InputfieldTypes.SELECT}
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                options={accountTermOptions}
                showSearch={field?.showSearch}
                onBlur={(e) => {
                  if (e.target.value) {
                    clearErrors(field?.name);
                  }
                }}
              />
            </ConditionalRender>
            <ConditionalRender
              visible={
                field?.type === InputfieldTypes.TEXT &&
                field?.name === "accountTermsCustom" &&
                accountTermsValue === "Custom Input"
              }
            >
              <InputField
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                type={InputfieldTypes.TEXT}
                prefix={field?.prefix}
                formattedNumberType={true}
                large={true}
                onBlur={(e) => {
                  if (e.target.value) {
                    clearErrors(field?.name);
                  }
                }}
              />
            </ConditionalRender>
          </>
        ))}
      </div>
      <div className={styles.formBottomWrapper}>
        <span className={styles.formLabels}>Pit Open & Close</span>
        {Object?.entries?.(dayTimeFields)?.map(([day, fieldList]) => {
          const switchVal = getSwitchVal(day);
          const fieldsToMap = fieldList.slice(1);
          return (
            <div className={styles.daySwitchWrapper} key={day}>
              {fieldsToMap?.map((field) => {
                return (
                  <>
                    <ConditionalRender
                      visible={field?.type === InputfieldTypes.SWITCH}
                    >
                      <div className={styles.daySwitch}>
                        <div className={styles.switchButton}>
                          <InputField
                            type={InputfieldTypes.SWITCH}
                            name={field?.name}
                            onChange={field?.onChange}
                            value={getSwitchVal(field?.label)}
                            defaultSwitch={watch(field?.name)}
                          />
                        </div>
                      </div>

                      <div className={styles.dayLabel}>
                        <div>{field?.label}</div>
                      </div>
                    </ConditionalRender>
                    <ConditionalRender
                      visible={
                        field?.type === InputfieldTypes.TIME_PICKER && switchVal
                      }
                    >
                      <div className={styles.timePickerWrapper}>
                        <InputField
                          type={InputfieldTypes.TIME_PICKER}
                          name={field?.name}
                          placeholder={field?.placeholder}
                          onBlur={(e) => {
                            if (e.target.value) {
                              clearErrors(field?.name);
                            }
                          }}
                        />
                      </div>
                    </ConditionalRender>
                  </>
                );
              })}
              <ConditionalRender visible={!switchVal}>
                <div className={styles.closedLabelContainer}>
                  <div className={styles.closedLabel}>Closed</div>
                </div>
              </ConditionalRender>
              <div className={styles.copyIcon}>
                <Popover content={DrawerMessages.COPY_PASTE_TOOLTIP}>
                  <img
                    src={copyIcon}
                    className={styles.pasteIcon}
                    onClick={() => handlePasteTimes(day)}
                    alt="Copy times"
                  />
                </Popover>
              </div>
            </div>
          );
        })}
      </div>{" "}
      <div className={styles.notesWrapper}>
        <InputField
          type={InputfieldTypes.TEXT_AREA}
          name="notes"
          label="Notes"
        />
      </div>
    </Drawer>
  );
};

export default AddQuarryDrawer;
